import { useBrand } from "@/context/BrandContext";
import { useTheme } from "@/context/ThemeContext";
import { useEffect, useMemo, useState } from "react";

/**
 * Le Desk logo. Falls back to a yellow/gold square with a bold "D"
 * until the admin uploads a custom logo (light + dark variants).
 *
 * Spec 2026-08-02 point #2 (rev) — pour rendre le logo en blanc uni sur
 * fond sombre, on applique un filtre CSS `brightness(0) invert(1)`. Ce
 * filtre repeint tous les pixels OPAQUES en blanc, en préservant l'alpha.
 * Il ne fonctionne donc PROPREMENT QUE si l'image a un canal alpha
 * (fond transparent avec forme du V découpée).
 *
 * Bug 2026-08-02 : le variant `logo_dark` uploadé en prod est 100%
 * opaque (fond baked-in) → le filtre transformait tout en carré blanc
 * plein. Fix : détecter l'alpha via Canvas au chargement de l'image ;
 * si absent, basculer sur l'autre variant si lui l'a, sinon désactiver
 * le filtre pour éviter le "carré blanc" (préférable au bug visuel).
 */
export default function Logo({ size = 40, withWordmark = false, className = "" }) {
  const { brand } = useBrand();
  const { theme } = useTheme();

  const preferred = theme === "light" ? brand.logo_light : brand.logo_dark;
  const fallback  = theme === "light" ? brand.logo_dark  : brand.logo_light;

  // Détection alpha runtime pour chaque URL candidate (data:image/png;base64
  // sont same-origin → Canvas 2D readable). On échantillonne max 128×128 px
  // pour la perf. Résultat en cache par URL, ne se recalcule pas.
  const [alphaMap, setAlphaMap] = useState({});
  useEffect(() => {
    const toTest = [preferred, fallback].filter(Boolean).filter((u) => !(u in alphaMap));
    if (!toTest.length) return;
    toTest.forEach((u) => {
      const img = new Image();
      img.onload = () => {
        try {
          const canvas = document.createElement("canvas");
          canvas.width = Math.min(img.naturalWidth || 128, 128);
          canvas.height = Math.min(img.naturalHeight || 128, 128);
          const ctx = canvas.getContext("2d");
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
          const d = ctx.getImageData(0, 0, canvas.width, canvas.height).data;
          let transparent = 0;
          const total = d.length / 4;
          for (let i = 3; i < d.length; i += 4) if (d[i] < 30) transparent++;
          setAlphaMap((m) => ({ ...m, [u]: transparent / total > 0.15 }));
        } catch {
          // getImageData tainted (cross-origin) → assume alpha to preserve filter behavior.
          setAlphaMap((m) => ({ ...m, [u]: true }));
        }
      };
      img.onerror = () => setAlphaMap((m) => ({ ...m, [u]: true }));
      img.src = u;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preferred, fallback]);

  // Choix final : privilégie le variant avec alpha pour permettre un filtre
  // propre. Sinon utilise le préféré tel quel sans filtre.
  const { url, applyFilter, isFallback } = useMemo(() => {
    const prefHasAlpha = preferred ? alphaMap[preferred] : undefined;
    const fbHasAlpha   = fallback  ? alphaMap[fallback]  : undefined;
    if (preferred && prefHasAlpha) return { url: preferred, applyFilter: true, isFallback: false };
    if (fallback  && fbHasAlpha)   return { url: fallback,  applyFilter: true, isFallback: !preferred };
    if (preferred) return { url: preferred, applyFilter: false, isFallback: false };
    if (fallback)  return { url: fallback,  applyFilter: false, isFallback: true };
    return { url: null, applyFilter: false, isFallback: false };
  }, [preferred, fallback, alphaMap]);

  const filter = !applyFilter
    ? "none"
    : (theme === "light" ? "brightness(0)" : "brightness(0) invert(1)");

  if (url) {
    return (
      <div
        className={`flex items-center gap-3 ${className}`}
        data-testid="brand-logo-image"
        data-fallback={isFallback ? "1" : "0"}
      >
        <img
          src={url}
          alt="Logo Vertec"
          style={{
            height: size,
            width: "auto",
            maxWidth: size * 4,
            objectFit: "contain",
            filter,
          }}
        />
        {withWordmark && (
          <div className="flex flex-col leading-tight">
            <span className="text-base font-semibold" style={{ color: "hsl(var(--foreground))" }}>
              Le Desk
            </span>
            <span className="text-xs" style={{ color: "hsl(var(--muted-foreground))" }}>
              Easy for all
            </span>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={`flex items-center gap-3 ${className}`} data-testid="brand-logo-default">
      <div
        style={{
          width: size,
          height: size,
          background: "#F5C842",
          borderRadius: 6,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#1B1B1B",
          fontWeight: 800,
          fontSize: size * 0.55,
          fontFamily: "Tahoma, sans-serif",
          letterSpacing: -1,
        }}
      >
        D
      </div>
      {withWordmark && (
        <div className="flex flex-col leading-tight">
          <span className="text-base font-semibold" style={{ color: "hsl(var(--foreground))" }}>
            Le Desk
          </span>
          <span className="text-xs" style={{ color: "hsl(var(--muted-foreground))" }}>
            Easy for all
          </span>
        </div>
      )}
    </div>
  );
}
